
import { createContext, useEffect, useState, useRef } from "react";
import api, { setUnauthorizedHandler } from "../api";
import { connectWebSocket } from "../ws";
export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);
  const wsRef = useRef(null);
  const wsListenersRef = useRef([]); // Array of callbacks for ws messages

  // Beim Start Session prüfen und ggf. WebSocket verbinden
  useEffect(() => {
    (async () => {
      try {
  const { data } = await api.get("/auth/me");
  setUser(data.user);
        // WebSocket verbinden, wenn User vorhanden
        if (data.user) {
          connectWS();
        }
      } catch {
        setUser(null);
      } finally {
        setReady(true);
      }
    })();
    // Cleanup: WebSocket schließen beim App-Unmount
    return () => {
      disconnectWS();
    };
  }, []);

  // Referral-Parameter früh cachen, damit er auch nach evtl. Redirect verfügbar bleibt
  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search);
      const ref = sp.get('ref');
      if (ref) localStorage.setItem('apn_ref', ref);
    } catch {}
  }, []);

  // React to 401 globally: log out and optionally redirect
  useEffect(() => {
    const PUBLIC_UNAUTH_PATHS = [
      '/login','/register','/forgot-password','/reset-password',
      '/newsletter/confirm','/newsletter/unsubscribe'
    ];
    const handler = async () => {
      // Only treat as a forced logout if a token existed (session expired) or we were on a protected page
      const hadToken = !!localStorage.getItem('token');
      const p = window.location.pathname || '';
      const isPublic = PUBLIC_UNAUTH_PATHS.some(base => p === base || p.startsWith(base + '/'));
      try { await api.post('/auth/logout'); } catch {}
      localStorage.removeItem('token');
      setUser(null);
      disconnectWS();
      // Skip redirect for public unauth pages without prior session
      if (hadToken && !isPublic) {
        try { window.location.href = '/login'; } catch {}
      }
    };
    setUnauthorizedHandler(handler);
    const evtHandler = () => handler();
    window.addEventListener('apn:unauthorized', evtHandler);
    return () => {
      window.removeEventListener('apn:unauthorized', evtHandler);
      setUnauthorizedHandler(null);
    };
  }, []);

  // WebSocket verbinden
  function connectWS() {
    if (wsRef.current) return; // schon verbunden
    const token = localStorage.getItem("token");
    if (!token) return;
    wsRef.current = connectWebSocket(token, (data) => {
      wsListenersRef.current.forEach((cb) => cb(data));
    });
  }

  // WebSocket trennen
  function disconnectWS() {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {}
      wsRef.current = null;
    }
  }
  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    disconnectWS();
    localStorage.removeItem("token");
  };

  // Nach Login WebSocket verbinden
  useEffect(() => {
    if (user) {
      connectWS();
    } else {
      disconnectWS();
    }
  }, [user]);

  // Frontend Auto-Logout bei Inaktivität, wenn konfiguriert
  useEffect(() => {
    let timer = null;
    const clear = () => { if (timer) { clearTimeout(timer); timer = null; } };
    const reset = () => {
      clear();
      const minutes = Number(user?.settings?.session?.autoLogoutMinutes || 0);
      const stay = user?.settings?.session?.stayLoggedIn === true;
      if (!user || stay || !minutes || isNaN(minutes) || minutes <= 0) return;
      timer = setTimeout(async () => {
        // Auto-logout
        try { await api.post('/auth/logout'); } catch {}
        localStorage.removeItem('token');
        setUser(null);
        disconnectWS();
        try { alert('Du wurdest aufgrund von Inaktivität automatisch ausgeloggt.'); } catch {}
        try {
          const p = window.location.pathname;
          if (p !== '/login' && p !== '/register') window.location.href = '/login';
        } catch {}
      }, minutes * 60 * 1000);
    };
    const events = ['mousemove','keydown','click','scroll','touchstart'];
    const onActivity = () => reset();
    if (user) {
      reset();
      events.forEach(ev => window.addEventListener(ev, onActivity));
    }
    return () => {
      clear();
      events.forEach(ev => window.removeEventListener(ev, onActivity));
    };
  }, [user?.settings?.session?.autoLogoutMinutes, user?.settings?.session?.stayLoggedIn, user]);

  // React to maintenance updates via WebSocket
  useEffect(() => {
    // Subscribe only when ws is connected and user exists
    if (!user) return;
    const unsubscribe = subscribeWS(async (data) => {
      if (!data || (data.type !== 'maintenance_update' && data.type !== 'maintenance:update')) return;
      const enabled = !!data.maintenance?.enabled;
      try {
        if (enabled) {
          // Force logout for everyone and reload
          try { await api.post('/auth/logout'); } catch {}
          localStorage.removeItem('token');
          setUser(null);
          // small delay to let cookie clear
          setTimeout(() => window.location.reload(), 100);
        } else {
          // Maintenance disabled -> refresh to reflect new state
          window.location.reload();
        }
      } catch {}
    });
    return () => { if (unsubscribe) unsubscribe(); };
  }, [user]);

  // Ermögliche Komponenten, sich für WS-Nachrichten zu registrieren
  const subscribeWS = (cb) => {
    wsListenersRef.current.push(cb);
    return () => {
      wsListenersRef.current = wsListenersRef.current.filter((x) => x !== cb);
    };
  };
  return (
    <UserContext.Provider value={{ user, setUser, logout, ready, subscribeWS }}>
      {children}
    </UserContext.Provider>
  );
};
